import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { NameDto } from 'api/Common/Types'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { CountryDto } from 'api/Identity/Types/Country'
import { DateFormatType } from 'api/Identity/Types/DateFormatType'
import { DefinedFilter } from 'api/Identity/Types/DefinedFilter'
import { DepartmentDto } from 'api/Identity/Types/DepartmentDto'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RegionDto } from 'api/Identity/Types/Region'
import { RoleDto } from 'api/Identity/Types/Role'
import { TeamDto } from 'api/Identity/Types/Team'
import { UserFilterType } from 'api/Identity/Types/UserFilterType'
import { SimpleUserResponse } from 'api/Identity/Types/UserResponse'
import { UserStatusDto } from 'api/Identity/Types/UserStatus'
import { TokenStorage } from 'api/tokenStorage'
import { PURGE } from 'redux-persist'

import { countriesConfig } from './countries'

const initialState: IdentityState = {
  regions: [],
  countries: [],
  tickmillCompanies: [],
  myTickmillCompanies: [],
  filter: [],
  filterType: [],
  dateFormat: [],
  teams: [],
  roles: [],
  permissions: [],
  administrationUsers: [],
  departments: [],
  userKinds: [],
  userStatuses: [],
}

export interface IdentityState {
  regions: RegionDto[]
  countries: CountryDto[]
  tickmillCompanies: TickmillCompanyDto[]
  myTickmillCompanies: TickmillCompanyDto[]
  filter: DefinedFilter[]
  filterType: UserFilterType[]
  dateFormat: DateFormatType[]
  teams: TeamDto[]
  roles: RoleDto[]
  permissions: PermissionDto[]
  administrationUsers: SimpleUserResponse[]
  departments: DepartmentDto[]
  userStatuses: UserStatusDto[]
  userKinds: NameDto[]
}

export const identitySlice = createSlice({
  name: 'identity',
  initialState,
  reducers: {
    fetchAdministrationUsers: (state, action: PayloadAction<SimpleUserResponse[]>) => {
      state.administrationUsers = action.payload
    },
    fetchPermissions: (state, action: PayloadAction<PermissionDto[]>) => {
      state.permissions = action.payload
    },
    fetchRoles: (state, action: PayloadAction<RoleDto[]>) => {
      state.roles = action.payload
    },
    fetchTeams: (state, action: PayloadAction<TeamDto[]>) => {
      state.teams = action.payload.sort((a, b) => a.name.localeCompare(b.name))
    },
    fetchRegions: (state, action: PayloadAction<RegionDto[]>) => {
      state.regions = action.payload
    },
    fetchCountries: (state, action: PayloadAction<CountryDto[]>) => {
      const countryCodeNumbers: { [key: string]: string[] } = {}
      countriesConfig.map((ccn) => {
        if (!ccn.countryCodeNumber) return null
        if (countryCodeNumbers[ccn.name])
          return countryCodeNumbers[ccn.name].push(ccn.countryCodeNumber)
        return (countryCodeNumbers[ccn.name] = [ccn.countryCodeNumber])
      })
      state.countries = action.payload.map((country) => ({
        ...country,
        countryCodeNumber: countryCodeNumbers[country.name],
      }))
    },
    fetchTickmillCompanies: (state, action: PayloadAction<TickmillCompanyDto[]>) => {
      state.tickmillCompanies = action.payload
    },
    fetchMyTickmillCompanies: (state, action: PayloadAction<TickmillCompanyDto[]>) => {
      state.myTickmillCompanies = action.payload
    },
    fetchDateFormat: (state, action: PayloadAction<DateFormatType[]>) => {
      state.dateFormat = action.payload
    },
    fetchFilterTypes: (state, action: PayloadAction<UserFilterType[]>) => {
      state.filterType = action.payload
    },
    fetchFilters: (state, action: PayloadAction<DefinedFilter[]>) => {
      state.filter = action.payload
    },
    fetchFilter: (state, action: PayloadAction<DefinedFilter>) => {
      state.filter.push(action.payload)
    },
    deleteFilter: (state, action: PayloadAction<string>) => {
      state.filter = state.filter.filter((x) => x.id !== action.payload)
    },
    fetchDepartments: (state, action: PayloadAction<DepartmentDto[]>) => {
      state.departments = action.payload
    },
    fetchUserStatuses: (state, action: PayloadAction<UserStatusDto[]>) => {
      state.userStatuses = action.payload
    },
    fetchUserKinds: (state, action: PayloadAction<NameDto[]>) => {
      state.userKinds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => {
      TokenStorage.clear()
      localStorage.clear()
    })
  },
})
